import { Search, X } from 'lucide-react';
import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { usePageLine } from '../hooks/usePageLine';

interface ReportPageScaffoldProps {
  /** Page title rendered in the header `<h1>` (e.g. "Daily Attendance"). */
  title: string;
  /**
   * The line under the title saying what the page is for. Left out by every
   * call site today: the scaffold then shows the catalogue's line for the current
   * address (navigation/catalog), the same words the section front page uses for
   * this page. Pass it only when a page must say something else; an address the
   * catalogue does not list, with no description passed, draws no line.
   *
   * Always ONE line, however long the text: it is cut with an ellipsis where the
   * header ends and the whole of it is the line's hover text.
   */
  description?: string;
  /** Current search-input value (bound verbatim from the call site). */
  searchValue: string;
  /** onChange handler for the search input (verbatim from the call site). */
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Placeholder text for the search input. */
  searchPlaceholder: string;
  /**
   * Extra right-padding utility class on the search input. Defaults to the
   * majority value (`pr-3`, used by Daily/Random Attendance); the Grooming page
   * passes `pr-4` so its appearance is unchanged.
   */
  searchInputPaddingRight?: string;
  /**
   * Filter controls rendered to the right of the search box inside the filters
   * row (date range filter, status dropdown, Download button, …). Each call
   * site passes its own controls verbatim, so layouts that differ structurally
   * keep their exact output.
   */
  filters: ReactNode;
  /**
   * Optional VIEW switcher rendered as its own row at the top of the Content
   * Card, above the filters row. Kept separate from `filters` on purpose: a tab
   * changes what the page is showing, a filter narrows what is already shown,
   * and crowding the two into one row makes the first look like the second.
   * Omitted by every page that has a single view, which then renders exactly as
   * before — this slot adds no markup when absent.
   */
  tabs?: ReactNode;
  /**
   * The page is one tab of a shell that already draws the title and the sentence
   * under it (Store Visit Compliance). Only the Content Card is drawn, so the screen
   * keeps a single heading; `title` then names the card for screen readers instead.
   * Left out by every other page, which renders exactly as before.
   */
  embedded?: boolean;
  /**
   * Everything rendered inside the Content Card `<section>` below the filters
   * row (error banner, loading state, table, pagination footer). Passed
   * verbatim so each page's body is unchanged.
   */
  children: ReactNode;
}

/**
 * Outer scaffold shared by the report/log pages (DailyAttendanceLogsPage,
 * GroomingReportPage, RandomAttendanceLogsPage): the `space-y-6` wrapper, the
 * header `<h1>`, the Content Card `<section>`, and the filters row with its
 * leading search input. Extracted from the byte-identical markup that was
 * inlined in each page. The per-page title, search bindings, filter controls
 * (date range / status / download), and section body are passed as props /
 * slots so every call site renders exactly as before.
 *
 * The one thing added since: the line under the title (see `description`), so
 * every page on this scaffold says what it is for without each page writing its
 * own. It never takes more than one line.
 */
export default function ReportPageScaffold({
  title,
  description,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchInputPaddingRight = 'pr-3',
  filters,
  tabs,
  embedded = false,
  children,
}: ReportPageScaffoldProps) {
  const pageLine = usePageLine();
  const line = description ?? pageLine;

  // A × inside the search box while it holds text: a link can open a page with the box
  // already filled (hooks/useLinkedSearch), and the box is then the only sign that the
  // list is narrowed, so the way out has to be in plain sight.
  // The pages pass an onChange, not a setter, so the × empties the box the way the
  // keyboard would: it sets the input's own value and lets React report the change.
  // Each page's handler then runs as it always does, with a real event to read. (The
  // value is set through the prototype because React watches the input's own `value`
  // property and would otherwise see nothing to report.)
  const searchInput = useRef<HTMLInputElement>(null);
  const clearSearch = () => {
    const input = searchInput.current;
    if (!input) return;
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    // The button is gone once the box is empty; leave the person in the box.
    input.focus();
  };

  const card = (
    <section className="rounded-lg border border-gray-100 bg-white p-6" aria-label={embedded ? title : undefined}>
      {/* View switcher (optional) — its own row, above the filters. */}
      {tabs && <div className="mb-4">{tabs}</div>}

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={searchInput}
            type="text"
            value={searchValue}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            className={`w-full pl-10 ${searchValue ? 'pr-8' : searchInputPaddingRight} py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
          />
          {searchValue && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {filters}
      </div>

      {children}
    </section>
  );

  if (embedded) return card;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        {/* min-w-0: lets the line be cut at the header's edge instead of widening the page. */}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {line && (
            <p className="mt-1 truncate text-sm text-gray-500" title={line}>
              {line}
            </p>
          )}
        </div>
      </div>

      {/* Content Card */}
      {card}
    </div>
  );
}
